import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AsyncBoundary, ConfirmDialog, Icon, Notice, useToast } from '@kollez/design-system';
import { apiGet, apiPatch, apiPost } from '@/lib/api';

/**
 * /platform/plans/:id       — read-only plan detail
 * /platform/plans/:id/edit  — same page, edit mode
 *
 * Two modes over the same server plan doc (`GET /platform/plans/:id`).
 * Read mode shows the price + features + full limits table.
 * Edit mode reveals the same form the /platform/plans list drawer used,
 * writing via `PATCH /platform/plans/:id`. New-plan creation stays in the
 * list-page drawer — this page is per-plan only.
 */

interface PlanFeatures {
  drivesAndOffers?: boolean;
  assessmentsModule?: boolean;
  nexie?: boolean;
  groupOverview?: boolean;
  nirfNaacExports?: boolean;
  sso?: boolean;
  customerSuccessManager?: boolean;
}
interface PlanLimits {
  minColleges?: number | null;
  maxColleges?: number | null;
  maxStudents?: number | null;
  aiMonthlyBudgetInr?: number;
  aiPerCollegeBudgetInr?: number;
  contractMonths?: number | null;
  supportChannels?: string[];
}
interface Plan {
  _id: string;
  code: string;
  name: string;
  description?: string;
  price: { amount: number | null; unit: string; currency: string };
  applicableFor?: string[];
  status: 'Live' | 'Draft' | 'Retired';
  features: PlanFeatures;
  limits: PlanLimits;
}

const FEATURE_LABELS: Array<[keyof PlanFeatures, string]> = [
  ['drivesAndOffers', 'Drives and offers'],
  ['assessmentsModule', 'Assessments module'],
  ['nexie', 'Nexie'],
  ['groupOverview', 'Group overview'],
  ['nirfNaacExports', 'NIRF and NAAC exports'],
  ['sso', 'SSO'],
  ['customerSuccessManager', 'Customer success manager'],
];

interface Draft {
  code: string;
  name: string;
  description: string;
  priceAmount: string;
  priceUnit: string;
  applicableFor: string;
  status: 'Live' | 'Draft' | 'Retired';
  features: PlanFeatures;
  limits: {
    minColleges: string; maxColleges: string; maxStudents: string;
    aiMonthlyBudgetInr: string; aiPerCollegeBudgetInr: string;
    contractMonths: string; supportChannels: string;
  };
}
function planToDraft(p: Plan): Draft {
  return {
    code: p.code,
    name: p.name,
    description: p.description ?? '',
    priceAmount: p.price.amount == null ? '' : String(p.price.amount),
    priceUnit: p.price.unit ?? 'per_year',
    applicableFor: (p.applicableFor ?? []).join(', '),
    status: p.status,
    features: { ...p.features },
    limits: {
      minColleges: p.limits.minColleges == null ? '' : String(p.limits.minColleges),
      maxColleges: p.limits.maxColleges == null ? '' : String(p.limits.maxColleges),
      maxStudents: p.limits.maxStudents == null ? '' : String(p.limits.maxStudents),
      aiMonthlyBudgetInr: p.limits.aiMonthlyBudgetInr == null ? '' : String(p.limits.aiMonthlyBudgetInr),
      aiPerCollegeBudgetInr: p.limits.aiPerCollegeBudgetInr == null ? '' : String(p.limits.aiPerCollegeBudgetInr),
      contractMonths: p.limits.contractMonths == null ? '' : String(p.limits.contractMonths),
      supportChannels: (p.limits.supportChannels ?? []).join(', '),
    },
  };
}
function draftToPayload(d: Draft) {
  const n = (s: string) => (s.trim() === '' ? null : Number(s));
  const list = (s: string) => s.split(',').map((x) => x.trim()).filter(Boolean);
  return {
    code: d.code.trim(),
    name: d.name.trim(),
    description: d.description || undefined,
    price: { amount: n(d.priceAmount), unit: d.priceUnit, currency: 'INR' },
    applicableFor: list(d.applicableFor),
    status: d.status,
    features: d.features,
    limits: {
      minColleges: n(d.limits.minColleges),
      maxColleges: n(d.limits.maxColleges),
      maxStudents: n(d.limits.maxStudents),
      aiMonthlyBudgetInr: d.limits.aiMonthlyBudgetInr ? Number(d.limits.aiMonthlyBudgetInr) : undefined,
      aiPerCollegeBudgetInr: d.limits.aiPerCollegeBudgetInr ? Number(d.limits.aiPerCollegeBudgetInr) : undefined,
      contractMonths: n(d.limits.contractMonths),
      supportChannels: list(d.limits.supportChannels),
    },
  };
}

function fmtColleges(l: PlanLimits): string {
  if (l.minColleges == null && l.maxColleges == null) return '—';
  if (l.minColleges === l.maxColleges) return String(l.minColleges);
  if (l.minColleges != null && l.maxColleges != null) return `${l.minColleges}–${l.maxColleges}`;
  if (l.minColleges != null) return `${l.minColleges}+`;
  return `up to ${l.maxColleges}`;
}
function fmtStudents(l: PlanLimits): string {
  return l.maxStudents == null ? 'Unlimited' : l.maxStudents.toLocaleString('en-IN');
}
function fmtAI(l: PlanLimits): string {
  if (l.aiPerCollegeBudgetInr) return `₹${l.aiPerCollegeBudgetInr.toLocaleString('en-IN')} per college / month`;
  return l.aiMonthlyBudgetInr ? `₹${l.aiMonthlyBudgetInr.toLocaleString('en-IN')} / month` : '—';
}
function fmtContract(l: PlanLimits): string {
  if (l.contractMonths == null) return 'Custom';
  if (l.contractMonths <= 6) return `${l.contractMonths} months`;
  if (l.contractMonths === 12) return '12 months';
  return `${Math.round(l.contractMonths / 12)} years`;
}

export function PlatformPlanDetailPage({ mode = 'view' }: { mode?: 'view' | 'edit' }) {
  const { id = '' } = useParams();
  const nav = useNavigate();
  const toast = useToast();
  const qc = useQueryClient();

  const q = useQuery({
    queryKey: ['platform', 'plan', id],
    enabled: !!id,
    queryFn: () => apiGet<{ plan: Plan } | Plan>(`/platform/plans/${id}`),
  });
  // API returns `{plan}` in some builds and the raw doc in others — normalise.
  const plan: Plan | undefined = q.data
    ? (('plan' in (q.data as object) ? (q.data as { plan: Plan }).plan : (q.data as Plan)))
    : undefined;

  const [draft, setDraft] = useState<Draft | null>(null);
  const [retireOpen, setRetireOpen] = useState(false);
  useEffect(() => {
    if (plan && mode === 'edit' && !draft) setDraft(planToDraft(plan));
  }, [plan, mode, draft]);

  const update = useMutation({
    mutationFn: () => apiPatch(`/platform/plans/${id}`, draftToPayload(draft!)),
    onSuccess: () => {
      toast.show({ message: 'Plan updated.' });
      void qc.invalidateQueries({ queryKey: ['platform', 'plans'] });
      void qc.invalidateQueries({ queryKey: ['platform', 'plan', id] });
      nav(`/platform/plans/${id}`);
    },
    onError: (e) => toast.show({ tone: 'warning', message: (e as Error).message }),
  });

  const retire = useMutation({
    mutationFn: () => apiPost(`/platform/plans/${id}/retire`, {}),
    onSuccess: () => {
      toast.show({ message: 'Plan retired.' });
      void qc.invalidateQueries({ queryKey: ['platform', 'plans'] });
      void qc.invalidateQueries({ queryKey: ['platform', 'plan', id] });
      setRetireOpen(false);
    },
    onError: (e) => toast.show({ tone: 'warning', message: (e as Error).message }),
  });

  const priceLabel = plan?.price.amount == null ? 'Custom' : `₹${plan.price.amount.toLocaleString('en-IN')}`;

  return (
    <div className="stack">
      <div className="page-header">
        <div className="ph-text">
          <p className="eyebrow"><Link to="/platform/plans">← All plans</Link></p>
          <h1 className="h-page">{plan?.name ?? 'Plan'}</h1>
          {plan && (
            <p className="subhead">
              <code>{plan.code}</code> · {plan.status}
              {plan.applicableFor && plan.applicableFor.length > 0 && (
                <> · for {plan.applicableFor.join(', ')}</>
              )}
            </p>
          )}
        </div>
        <div className="ph-actions">
          {plan && mode === 'view' && (
            <>
              {plan.status !== 'Retired' && (
                <button
                  type="button"
                  className="btn btn-ghost btn-danger"
                  onClick={() => setRetireOpen(true)}
                  disabled={retire.isPending}
                >
                  {retire.isPending ? 'Retiring…' : 'Retire plan'}
                </button>
              )}
              <Link className="btn btn-primary" to={`/platform/plans/${id}/edit`}>
                <Icon name="pencil" size={14} />Edit plan
              </Link>
            </>
          )}
          {mode === 'edit' && (
            <Link className="btn btn-ghost" to={`/platform/plans/${id}`}>Cancel</Link>
          )}
        </div>
      </div>

      <AsyncBoundary
        kind="detail"
        loading={q.isLoading}
        error={q.error}
        empty={!q.isLoading && !plan}
        emptyTitle="Plan not found"
        onRetry={() => void q.refetch()}
      >
        {plan && mode === 'view' && (
          <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_320px]">
            <div className="stack">
              <div className="card card-pad">
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 12 }}>
                  <span style={{ fontFamily: '"Newsreader", Georgia, serif', fontSize: '2.5rem', fontWeight: 500, lineHeight: 1 }}>
                    {priceLabel}
                  </span>
                  <span className="cell-sub">{plan.price.unit.replace(/_/g, ' ')}</span>
                  <span className={`badge ml-auto ${plan.status === 'Live' ? 'badge-success' : plan.status === 'Retired' ? 'badge-danger' : ''}`}>
                    {plan.status}
                  </span>
                </div>
                {plan.description && (
                  <p className="mt-3" style={{ whiteSpace: 'pre-wrap' }}>{plan.description}</p>
                )}
              </div>

              <div className="card card-pad">
                <h2 className="h-section mb-3">What's included</h2>
                <ul className="grid gap-1.5">
                  {FEATURE_LABELS.map(([k, label]) => {
                    const on = !!plan.features[k];
                    return (
                      <li key={k} className="flex gap-2 items-baseline text-sm">
                        <Icon name={on ? 'check' : 'x'} size={12} className={on ? 'text-success' : 'text-ink-tertiary'} />
                        <span className={on ? '' : 'text-ink-tertiary line-through'}>{label}</span>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </div>

            <div className="card card-pad">
              <h2 className="h-section mb-3">Limits</h2>
              <dl className="dl">
                <dt>Colleges</dt><dd>{fmtColleges(plan.limits)}</dd>
                <dt>Students</dt><dd>{fmtStudents(plan.limits)}</dd>
                <dt>Nexie AI budget</dt><dd>{fmtAI(plan.limits)}</dd>
                <dt>Contract</dt><dd>{fmtContract(plan.limits)}</dd>
                <dt>Support</dt><dd>{(plan.limits.supportChannels ?? []).join(' · ') || 'Email'}</dd>
              </dl>
            </div>
          </div>
        )}

        {plan && mode === 'edit' && draft && (
          <div className="card card-pad">
            <Notice tone="info">
              Changes apply to <b>new contracts only</b>. Existing tenants on this plan keep their
              current terms until renewal — their planSnapshot is frozen at contract-start.
            </Notice>

            <div className="grid gap-3 mt-4">
              <div className="grid grid-cols-2 gap-3">
                <label className="grid gap-1.5">
                  <span className="label">Code</span>
                  <input className="input" value={draft.code} disabled title="Plan code cannot be changed after creation." />
                </label>
                <label className="grid gap-1.5">
                  <span className="label">Name</span>
                  <input className="input" value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
                </label>
              </div>
              <label className="grid gap-1.5">
                <span className="label">Description</span>
                <textarea className="input" rows={2} value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })} />
              </label>
              <div className="grid grid-cols-3 gap-3">
                <label className="grid gap-1.5">
                  <span className="label">Price (₹) — empty for Custom</span>
                  <input className="input" value={draft.priceAmount} onChange={(e) => setDraft({ ...draft, priceAmount: e.target.value })} />
                </label>
                <label className="grid gap-1.5">
                  <span className="label">Unit</span>
                  <select className="input" value={draft.priceUnit} onChange={(e) => setDraft({ ...draft, priceUnit: e.target.value })}>
                    <option value="per_year">per year</option>
                    <option value="per_college_per_year">per college per year</option>
                    <option value="custom">custom</option>
                  </select>
                </label>
                <label className="grid gap-1.5">
                  <span className="label">Status</span>
                  <select className="input" value={draft.status} onChange={(e) => setDraft({ ...draft, status: e.target.value as Draft['status'] })}>
                    <option value="Draft">Draft</option>
                    <option value="Live">Live</option>
                    <option value="Retired">Retired</option>
                  </select>
                </label>
              </div>
              <label className="grid gap-1.5">
                <span className="label">Applicable for (comma-separated tenant kinds)</span>
                <input className="input" value={draft.applicableFor} onChange={(e) => setDraft({ ...draft, applicableFor: e.target.value })} placeholder="COLLEGE, GROUP, UNIVERSITY" />
              </label>

              <fieldset className="grid gap-2 well p-4">
                <legend className="text-sm font-medium">Features included</legend>
                {FEATURE_LABELS.map(([k, label]) => (
                  <label key={k} className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={!!draft.features[k]}
                      onChange={(e) => setDraft({ ...draft, features: { ...draft.features, [k]: e.target.checked } })}
                    />
                    {label}
                  </label>
                ))}
              </fieldset>

              <fieldset className="grid grid-cols-2 gap-3 well p-4">
                <legend className="text-sm font-medium">Limits</legend>
                <label className="grid gap-1.5"><span className="label">Min colleges</span><input className="input" value={draft.limits.minColleges} onChange={(e) => setDraft({ ...draft, limits: { ...draft.limits, minColleges: e.target.value } })} /></label>
                <label className="grid gap-1.5"><span className="label">Max colleges</span><input className="input" value={draft.limits.maxColleges} onChange={(e) => setDraft({ ...draft, limits: { ...draft.limits, maxColleges: e.target.value } })} /></label>
                <label className="grid gap-1.5"><span className="label">Max students</span><input className="input" value={draft.limits.maxStudents} onChange={(e) => setDraft({ ...draft, limits: { ...draft.limits, maxStudents: e.target.value } })} /></label>
                <label className="grid gap-1.5"><span className="label">Contract months</span><input className="input" value={draft.limits.contractMonths} onChange={(e) => setDraft({ ...draft, limits: { ...draft.limits, contractMonths: e.target.value } })} /></label>
                <label className="grid gap-1.5"><span className="label">Nexie AI ₹ / month</span><input className="input" value={draft.limits.aiMonthlyBudgetInr} onChange={(e) => setDraft({ ...draft, limits: { ...draft.limits, aiMonthlyBudgetInr: e.target.value } })} /></label>
                <label className="grid gap-1.5"><span className="label">Nexie AI ₹ / college / month</span><input className="input" value={draft.limits.aiPerCollegeBudgetInr} onChange={(e) => setDraft({ ...draft, limits: { ...draft.limits, aiPerCollegeBudgetInr: e.target.value } })} /></label>
                <label className="grid gap-1.5 col-span-2"><span className="label">Support channels (comma-separated)</span><input className="input" value={draft.limits.supportChannels} onChange={(e) => setDraft({ ...draft, limits: { ...draft.limits, supportChannels: e.target.value } })} placeholder="email, phone, whatsapp" /></label>
              </fieldset>

              <div className="flex gap-2 justify-end pt-2">
                <Link className="btn btn-ghost" to={`/platform/plans/${id}`}>Cancel</Link>
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={update.isPending || !draft.name.trim()}
                  onClick={() => update.mutate()}
                >
                  {update.isPending ? 'Saving…' : 'Save changes'}
                </button>
              </div>
            </div>
          </div>
        )}
      </AsyncBoundary>

      <ConfirmDialog
        open={retireOpen}
        onClose={() => setRetireOpen(false)}
        title="Retire this plan?"
        description="It stops showing in the tenant provisioning picker. Existing subscriptions keep working — their planSnapshot is frozen."
        confirmLabel="Retire"
        tone="danger"
        onConfirm={() => retire.mutate()}
      />
    </div>
  );
}
